<!--
@component
TeamCard

See markup below.
-->

<script lang="ts">
  import Card from './Card/index.svelte';
  import type { TeamStats } from './types';
  import { appState } from '$lib/stores/index.svelte';
  import getTeamPath from '$utils/getTeamPath';

  interface Props {
    /** The team information. */
    teamStats: TeamStats;
    /**
     * Render the card inside an `<a>` linking to the team page.
     * Falls back to an unlinked card if `teamStats.slug` is missing.
     * @default false
     */
    link?: boolean;
  }

  let { teamStats, link = false }: Props = $props();

  // Defer to `getTeamPath` so the embed branch (`appState.embed`)
  // flows through automatically — embed contexts get an absolute
  // production URL, everything else gets a SvelteKit-resolved
  // relative URL. Falling back to `null` avoids
  // `/teams/undefined/` if a slug ever goes missing.
  const href = $derived(
    link && teamStats.slug ?
      (getTeamPath({ slug: teamStats.slug, name: teamStats.name })?.url ?? null)
    : null
  );
</script>

{#if href}
  <a
    {href}
    target={appState.embed ? '_top' : undefined}
    aria-label={`View ${teamStats.name} – ${teamStats.points} points`}
    data-sveltekit-preload-data="hover"
    class:loading={appState.isLoading}
  >
    <Card {teamStats} />
  </a>
{:else}
  <Card {teamStats} />
{/if}

<style lang="scss">
  @use '../../../styles/mixins' as mixins;

  a {
    display: block;
    margin: auto;
    color: inherit;
    text-decoration: inherit;
    border-radius: var(--theme-style-border-radius);

    &.loading {
      pointer-events: none;
      cursor: default;
    }

    @include mixins.hover-supported() {
      opacity: 0.9;
    }
  }
</style>
